import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  KeyboardEvent,
  ReactElement,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebounce } from 'use-debounce';
import useTags from '../hooks/tags';
import { TagUsage } from '../types';
import classNames from '../utilities/class-names';
import { TAGS_MAX_COUNT, normaliseTag } from '../utilities/tags';
import './TagInput.scss';

const SUGGESTIONS_DEBOUNCE_MS = 150;

type TagInputProps = {
  id?: string;
  value: string[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
};

export function TagInput({
  id,
  value,
  onChange,
  disabled,
  placeholder = 'Add a tag...',
}: TagInputProps): ReactElement {
  const { fetchTags } = useTags();

  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<TagUsage[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [debouncedText] = useDebounce(
    normaliseTag(text),
    SUGGESTIONS_DEBOUNCE_MS
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  const full = value.length >= TAGS_MAX_COUNT;

  // Suggest the user's existing tags (most-used first) while the input is
  // focused, leaving out tags that have already been added
  useEffect(() => {
    if (!focused || full) {
      return;
    }

    const controller = new AbortController();
    fetchTags(debouncedText, controller.signal).then(tags => {
      if (!controller.signal.aborted) {
        setSuggestions(tags);
        setActiveIndex(-1);
      }
    });

    return () => controller.abort();
  }, [debouncedText, focused, full, fetchTags]);

  const visibleSuggestions = useMemo(
    () => suggestions.filter(suggestion => !value.includes(suggestion.tag)),
    [suggestions, value]
  );
  const showSuggestions = focused && !full && visibleSuggestions.length > 0;

  const addTags = useCallback(
    (tags: string[]) => {
      const added = tags
        .map(normaliseTag)
        .filter(tag => tag && !value.includes(tag));

      if (added.length > 0) {
        onChange([...value, ...new Set(added)].slice(0, TAGS_MAX_COUNT));
      }
    },
    [value, onChange]
  );

  const addTag = useCallback(
    (tag: string) => {
      setText('');
      setActiveIndex(-1);
      addTags([tag]);
    },
    [addTags]
  );

  // Spaces and commas separate tags. This is handled when the text changes
  // rather than on key presses, so that it also works for pasted text and for
  // mobile keyboards (which often don't say which key was pressed)
  const handleTextChange = (newText: string) => {
    const parts = newText.split(/[\s,]+/);

    if (parts.length > 1) {
      addTags(parts.slice(0, -1));
      setActiveIndex(-1);
    }

    setText(parts[parts.length - 1]);
  };

  const removeTag = useCallback(
    (tag: string) => {
      onChange(value.filter(t => t !== tag));
      inputRef.current?.focus();
    },
    [value, onChange]
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const count = visibleSuggestions.length;

    switch (e.key) {
      case 'Enter':
        // Don't submit the surrounding form while adding a tag
        if (showSuggestions && activeIndex >= 0) {
          e.preventDefault();
          addTag(visibleSuggestions[activeIndex].tag);
        } else if (text.trim()) {
          e.preventDefault();
          addTag(text);
        }
        break;

      case 'Backspace':
        if (!text && value.length > 0) {
          e.preventDefault();
          removeTag(value[value.length - 1]);
        }
        break;

      case 'ArrowDown':
      case 'ArrowUp':
        if (showSuggestions) {
          e.preventDefault();
          setActiveIndex(i =>
            e.key === 'ArrowDown' ? (i + 1) % count : i <= 0 ? count - 1 : i - 1
          );
        }
        break;

      case 'Escape':
        if (showSuggestions) {
          e.preventDefault();
          setSuggestions([]);
        }
        break;
    }
  };

  return (
    <div
      className={classNames(
        'tag-input',
        focused ? 'tag-input-focused' : undefined,
        disabled ? 'tag-input-disabled' : undefined
      )}
      onClick={() => inputRef.current?.focus()}
    >
      {value.map(tag => (
        <span key={tag} className="tag-input-tag">
          {tag}
          <button
            type="button"
            className="tag-input-remove"
            title={`Remove "${tag}"`}
            aria-label={`Remove tag ${tag}`}
            disabled={disabled}
            onClick={e => {
              e.stopPropagation();
              removeTag(tag);
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={text}
        placeholder={full ? `Up to ${TAGS_MAX_COUNT} tags` : placeholder}
        disabled={disabled || full}
        autoComplete="off"
        spellCheck={false}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showSuggestions}
        aria-controls={listboxId}
        aria-activedescendant={
          showSuggestions && activeIndex >= 0
            ? `${listboxId}-${activeIndex}`
            : undefined
        }
        onChange={e => handleTextChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          // Keep anything that was typed but not added yet
          if (text.trim()) {
            addTag(text);
          }
          setFocused(false);
        }}
      />
      <ul
        id={listboxId}
        role="listbox"
        className="tag-input-suggestions"
        hidden={!showSuggestions}
      >
        {visibleSuggestions.map((suggestion, i) => (
          <li
            key={suggestion.tag}
            id={`${listboxId}-${i}`}
            role="option"
            aria-selected={i === activeIndex}
            className={classNames(
              'tag-input-suggestion',
              i === activeIndex ? 'active' : undefined
            )}
            // Prevent the input losing focus before the click is handled
            onMouseDown={e => e.preventDefault()}
            onMouseMove={() => setActiveIndex(i)}
            onClick={() => addTag(suggestion.tag)}
          >
            <span>{suggestion.tag}</span>
            <span className="tag-input-suggestion-count">
              {suggestion.count}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
